import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CreditCard,
  Layers,
  Plus,
  Power,
  PowerOff,
  Store,
  User,
  Users,
  XCircle,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { formatDate, formatMoney } from "@/lib/utils";
import { PageHeader } from "@/components/data/toolbar";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/primitives";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/overlays";
import { ConfirmDialog } from "@/components/data/ConfirmDialog";

interface PlanItem {
  id: string;
  name: string;
  code: string;
  target_role: string;
  price: number;
  validity_days: number;
  contact_unlocks: number;
  max_listings: number;
  description: string | null;
  features: string[];
  is_active: boolean;
  subscribers_count: number;
  created_at: string;
}

interface SubscriptionItem {
  id: string;
  user_id: string;
  user_name: string;
  user_phone: string | null;
  user_email: string | null;
  user_role: string;
  plan_id: string;
  plan_name: string;
  plan_price: number;
  status: string;
  remaining_unlocks: number;
  start_date: string;
  end_date: string;
  is_expired: boolean;
  created_at: string;
}

export const PlansPage = () => {
  const queryClient = useQueryClient();

  const [tab, setTab] = React.useState<"plans" | "subscriptions">("plans");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [cancelSubId, setCancelSubId] = React.useState<string | null>(null);

  // Form State
  const [form, setForm] = React.useState({
    name: "",
    code: "",
    target_role: "buyer",
    price: 99,
    validity_days: 30,
    contact_unlocks: 5,
    max_listings: 0,
    description: "",
  });

  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ["plans"],
    queryFn: () => api.get<{ items: PlanItem[]; total: number }>("/plans"),
  });

  const { data: subsData, isLoading: subsLoading } = useQuery({
    queryKey: ["subscriptions"],
    queryFn: () => api.get<{ items: SubscriptionItem[]; total: number }>("/subscriptions"),
  });

  const createPlanMutation = useMutation({
    mutationFn: (body: typeof form) => api.postWithMessage("/plans", body),
    onSuccess: ({ message }) => {
      toast.success(message);
      setCreateOpen(false);
      setForm({
        name: "",
        code: "",
        target_role: "buyer",
        price: 99,
        validity_days: 30,
        contact_unlocks: 5,
        max_listings: 0,
        description: "",
      });
      void queryClient.invalidateQueries({ queryKey: ["plans"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "kpis"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Failed to create plan"),
  });

  const togglePlanMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api.putWithMessage(`/plans/${id}`, { is_active }),
    onSuccess: ({ message }) => {
      toast.success(message);
      void queryClient.invalidateQueries({ queryKey: ["plans"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Failed to update plan"),
  });

  const cancelSubMutation = useMutation({
    mutationFn: (id: string) => api.postWithMessage(`/subscriptions/${id}/cancel`),
    onSuccess: ({ message }) => {
      toast.success(message);
      setCancelSubId(null);
      void queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "kpis"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Failed to cancel subscription"),
  });

  const seedPlansMutation = useMutation({
    mutationFn: () => api.postWithMessage("/seed-plans"),
    onSuccess: ({ message }) => {
      toast.success(message);
      void queryClient.invalidateQueries({ queryKey: ["plans"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Failed to seed default plans"),
  });

  const plans = plansData?.items ?? [];
  const subs = subsData?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader
          title="Plans &amp; Subscriptions"
          description="Create and configure monetization plans, unlock credits, and subscriber memberships."
        />
        <div className="flex items-center gap-2">
          {plans.length === 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => seedPlansMutation.mutate()}
              loading={seedPlansMutation.isPending}
            >
              Seed Default Plans
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="gap-1.5 font-medium"
          >
            <Plus className="h-4 w-4" /> Create New Plan
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as "plans" | "subscriptions")}>
        <TabsList>
          <TabsTrigger value="plans" className="gap-2">
            <Layers className="h-4 w-4" /> Subscription Plans ({plans.length})
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="gap-2">
            <Users className="h-4 w-4" /> Active Subscribers ({subs.length})
          </TabsTrigger>
        </TabsList>

        {/* Plans Management Tab */}
        <TabsContent value="plans" className="mt-4 space-y-4">
          {plansLoading ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-64 w-full rounded-2xl" />
              ))}
            </div>
          ) : plans.length === 0 ? (
            <Card className="p-8">
              <EmptyState
                icon={Layers}
                title="No subscription plans found"
                description="Click 'Seed Default Plans' or create a custom plan to monetize contact unlocks and breeder listings."
                action={
                  <Button onClick={() => seedPlansMutation.mutate()} loading={seedPlansMutation.isPending}>
                    Seed Default Plans Now
                  </Button>
                }
              />
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {plans.map((p) => (
                <Card
                  key={p.id}
                  className={`border transition-all duration-200 ${
                    p.is_active ? "border-border hover:border-primary/50 shadow-sm" : "border-border/50 opacity-60 bg-surface/30"
                  }`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <Badge variant={p.target_role === "breeder" ? "secondary" : "default"} className="capitalize">
                        {p.target_role} Plan
                      </Badge>
                      <Badge variant={p.is_active ? "success" : "destructive"}>
                        {p.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <CardTitle className="mt-2 text-lg font-bold text-foreground">
                      {p.name}
                    </CardTitle>
                    <CardDescription className="text-xs font-mono">
                      CODE: {p.code}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-extrabold text-foreground">{formatMoney(p.price)}</span>
                      <span className="text-xs text-muted-foreground">/ {p.validity_days} days</span>
                    </div>

                    <div className="space-y-2 rounded-xl bg-surface/50 p-3 text-xs">
                      {p.target_role === "buyer" && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Breeder Unlocks:</span>
                          <span className="font-semibold text-foreground">{p.contact_unlocks} Contacts</span>
                        </div>
                      )}
                      {p.target_role === "breeder" && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Max Pet Listings:</span>
                          <span className="font-semibold text-foreground">{p.max_listings} Listings</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Total Subscribers:</span>
                        <span className="font-semibold text-primary">{p.subscribers_count} Users</span>
                      </div>
                    </div>

                    {p.description && (
                      <p className="text-xs text-muted-foreground">{p.description}</p>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          togglePlanMutation.mutate({ id: p.id, is_active: !p.is_active })
                        }
                        className={`gap-1.5 text-xs ${p.is_active ? "text-warning" : "text-success"}`}
                      >
                        {p.is_active ? (
                          <>
                            <PowerOff className="h-3.5 w-3.5" /> Deactivate
                          </>
                        ) : (
                          <>
                            <Power className="h-3.5 w-3.5" /> Activate
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="mt-4 space-y-4">
          {subsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          ) : subs.length === 0 ? (
            <Card className="p-8">
              <EmptyState
                icon={CreditCard}
                title="No active user subscriptions"
                description="When buyers or breeders purchase subscription plans or get assigned by admin, they will appear here."
              />
            </Card>
          ) : (
            <div className="space-y-3">
              {subs.map((s) => (
                <Card key={s.id} className="border-border hover:border-primary/40 transition-colors">
                  <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3.5">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                        {s.user_role === "breeder" ? <Store className="h-5 w-5" /> : <User className="h-5 w-5" />}
                      </span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-foreground">{s.user_name}</p>
                          <Badge variant="secondary" className="capitalize text-[10px]">{s.user_role}</Badge>
                          <Badge variant={s.status === "active" && !s.is_expired ? "success" : "secondary"}>
                            {s.is_expired ? "Expired" : s.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {s.user_phone ? `+91 ${s.user_phone}` : s.user_email || "—"} · Plan: <strong className="text-foreground">{s.plan_name}</strong> ({formatMoney(s.plan_price)})
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2">
                          <span>Valid: {formatDate(s.start_date)} &rarr; {formatDate(s.end_date)}</span>
                          <span>· Remaining Unlocks: <strong className="text-foreground">{s.remaining_unlocks}</strong></span>
                        </p>
                      </div>
                    </div>

                    {s.status === "active" && !s.is_expired && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCancelSubId(s.id)}
                        className="gap-1 text-xs text-destructive hover:text-destructive self-end sm:self-center"
                      >
                        <XCircle className="h-3.5 w-3.5" /> Cancel Subscription
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Plan Dialog Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Create New Subscription Plan</DialogTitle>
            <DialogDescription>
              Configure plan pricing, validity duration, and contact unlock limits.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              createPlanMutation.mutate(form);
            }}
            className="space-y-4"
          >
            <DialogBody className="space-y-3.5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    Plan Display Name *
                  </label>
                  <Input
                    required
                    placeholder="e.g. Buyer Gold Plan"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    Unique Plan Code *
                  </label>
                  <Input
                    required
                    placeholder="e.g. BUYER_GOLD_30"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    Target Role
                  </label>
                  <Select
                    value={form.target_role}
                    onValueChange={(v) => setForm({ ...form, target_role: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buyer">Buyer</SelectItem>
                      <SelectItem value="breeder">Breeder</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    Price (INR ₹) *
                  </label>
                  <Input
                    type="number"
                    required
                    min={0}
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    Validity (Days) *
                  </label>
                  <Input
                    type="number"
                    required
                    min={1}
                    value={form.validity_days}
                    onChange={(e) => setForm({ ...form, validity_days: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {form.target_role === "buyer" ? (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">
                      Included Breeder Contact Unlocks
                    </label>
                    <Input
                      type="number"
                      min={0}
                      value={form.contact_unlocks}
                      onChange={(e) => setForm({ ...form, contact_unlocks: Number(e.target.value) })}
                    />
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">
                      Max Pet Listings Allowed
                    </label>
                    <Input
                      type="number"
                      min={0}
                      value={form.max_listings}
                      onChange={(e) => setForm({ ...form, max_listings: Number(e.target.value) })}
                    />
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    Description
                  </label>
                  <Input
                    placeholder="Short description for buyers"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
              </div>
            </DialogBody>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createPlanMutation.isPending}
                className="gap-1.5"
              >
                Create Plan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <ConfirmDialog
        open={Boolean(cancelSubId)}
        onOpenChange={(open) => !open && setCancelSubId(null)}
        title="Cancel User Subscription?"
        description="This will immediately deactivate the subscription and stop further usage."
        confirmLabel="Cancel Subscription"
        variant="destructive"
        loading={cancelSubMutation.isPending}
        onConfirm={() => cancelSubId && cancelSubMutation.mutate(cancelSubId)}
      />
    </div>
  );
};
